import {applyOperation} from '@github-ui/optimistic-connection'
import {connectionMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {hashKey, type QueryKey, useQueryClient} from '@github-ui/react-query'
import type {CreateRepositoryLabelMutationMutation} from '@github-ui/server-persisted-queries/types/createRepositoryLabelMutation'
import {useCallback, useMemo, useRef} from 'react'

import {LABELS} from './constants/labels'
import type {LabelsQueryData} from './types'
import {
  buildConfirmedCreateLabelOperation,
  buildCreateLabelOperation,
  type LabelInput,
  labelInputFromOperation,
  LABELS_CONNECTION_PATH,
  newTempLabelId,
} from './utils/label-connection-operations'
import {createLabelRequest} from './utils/label-requests'

type UseCreateLabelOptions = {
  repositoryId: string
  repositoryNameWithOwner: string
  queryKey?: RouteQueryKey
  setSubmissionErrors: (v: string) => void
  setDialogOpen: (open: boolean) => void
  onSuccess?: () => void
}

type CreateLabelHandler = (input: LabelInput, setIsSubmitting: (isSubmitting: boolean) => void) => void

const CREATE_MUTATION_META_KEY = 'repositoryLabelCreate'

function getQueryPathname(queryKey: QueryKey): string | undefined {
  const queryDeps = queryKey[4]
  if (!queryDeps || typeof queryDeps !== 'object' || Array.isArray(queryDeps) || !('pathname' in queryDeps)) return
  return typeof queryDeps.pathname === 'string' ? queryDeps.pathname : undefined
}

export function useCreateLabel({
  repositoryId,
  repositoryNameWithOwner,
  queryKey,
  setSubmissionErrors,
  setDialogOpen,
  onSuccess,
}: UseCreateLabelOptions): CreateLabelHandler {
  const queryClient = useQueryClient()
  const setIsSubmittingRef = useRef<(isSubmitting: boolean) => void>(() => {})
  const mutationKey = useMemo(() => connectionMutationKey(repositoryId, LABELS_CONNECTION_PATH), [repositoryId])

  const {mutate} = useConnectionMutation<CreateRepositoryLabelMutationMutation, Error>({
    ownerId: repositoryId,
    connectionPath: LABELS_CONNECTION_PATH,
    meta: {[CREATE_MUTATION_META_KEY]: true},
    // The operation's node carries the raw input the write needs.
    mutationFn: async operation => {
      const input = labelInputFromOperation(operation)
      if (!input) throw new Error(LABELS.labelCreateError)
      return createLabelRequest(repositoryNameWithOwner, repositoryId, input)
    },
    onError: error => {
      setSubmissionErrors(error.message)
      setIsSubmittingRef.current(false)
      setDialogOpen(true)
    },
    onSuccess: (data, {operation}) => {
      const createdLabel = data.createLabel?.label
      const tempId = operation.type === 'addToConnection' ? operation.node.id : undefined
      if (queryKey && createdLabel && tempId) {
        queryClient.setQueryData<LabelsQueryData>(queryKey, base =>
          base ? applyOperation(base, repositoryId, buildConfirmedCreateLabelOperation(tempId, createdLabel)) : base,
        )
      }
      onSuccess?.()
    },
    onSettled: async () => {
      const pendingCreateCount = queryClient
        .getMutationCache()
        .findAll({mutationKey, status: 'pending'})
        .filter(mutation => mutation.options.meta?.[CREATE_MUTATION_META_KEY]).length
      if (!queryKey || pendingCreateCount > 1) return

      const pathname = getQueryPathname(queryKey)
      const sourceQueryHash = hashKey(queryKey)
      await queryClient.invalidateQueries({
        queryKey: queryKey.slice(0, 4),
        predicate: query =>
          pathname !== undefined &&
          getQueryPathname(query.queryKey) === pathname &&
          hashKey(query.queryKey) !== sourceQueryHash,
        refetchType: 'all',
      })
    },
  })

  return useCallback(
    (input: LabelInput, setIsSubmitting: (isSubmitting: boolean) => void) => {
      setIsSubmittingRef.current = setIsSubmitting
      setDialogOpen(false)
      mutate({operation: buildCreateLabelOperation(newTempLabelId(), input)})
    },
    [mutate, setDialogOpen],
  )
}
